export interface BlueprintItem {
  title: string;
  uxStrategy: string;
  description: string;
  visualSuggestion: string;
}

export type LeadStatus = 'novo' | 'apresentacao' | 'negociacao' | 'fechamento' | 'ganho' | 'perdido';

export interface Lead {
  id: string;
  name: string;
  phone: string;
  status: LeadStatus;
  createdAt: string;
  updatedAt: string;
  lastContactAt?: string;
  source: string; // e.g., 'facebook', 'google', 'whatsapp'
  value?: number;
  isDuplicate?: boolean;
  assignedTo?: string; // Sales rep name
  email?: string;
}

export interface CustomerService {
  id: string;
  title: string;
  amount: number;
  date: string;
  status: 'pago' | 'pendente' | 'cancelado';
  category?: string;
}

export interface CustomerTimelineEvent {
  id: string;
  type: 'lead_captured' | 'booking_created' | 'invoice_issued' | 'payment_received' | 'portal_accessed' | 'note';
  title: string;
  description: string;
  timestamp: string;
  amount?: number;
}

export interface CustomerProfile {
  id: string;
  clientId: string; // ID do dono da conta (ex: Thomas)
  name: string;
  phone: string; // Telefone normalizado para autenticação instantânea
  email?: string;
  type: 'lead' | 'cliente';
  status: string; // 'novo' | 'qualificado' | 'ativo' | 'vip' | 'recorrente' | 'perdido'
  createdAt: string;
  lastActivityAt?: string;
  totalRevenue: number; // LTV acumulado
  totalPending: number; // Saldo em aberto
  purchasedServices: CustomerService[];
  timeline: CustomerTimelineEvent[];
  notes?: string;
  portalAccessCode?: string;
  assignedTo?: string;
  source?: string;
}

export interface CustomerInvoice {
  id: string;
  invoiceNumber: string;
  clientId: string; // Dono do negócio
  clientSlug?: string;
  customerPhone: string;
  customerName: string;
  customerEmail?: string;
  title: string;
  description?: string;
  amount: number;
  dueDate: string;
  status: 'pending' | 'paid' | 'expired' | 'canceled';
  paymentMethodType?: 'pix' | 'card' | 'gateway';
  pixCode?: string;
  cardPaymentUrl?: string;
  paidAt?: string;
  createdAt: string;
  receiptUrl?: string;
}

export interface ClientPaymentConfig {
  clientId: string;
  pixKeyType?: 'cnpj' | 'cpf' | 'email' | 'phone' | 'random';
  pixKey?: string;
  pixReceiverName?: string;
  pixCity?: string;
  cardPaymentLink?: string;
  cardLinkInstructions?: string;
  gatewayProvider?: 'mercadopago' | 'asaas' | 'stripe' | 'none';
  gatewayToken?: string;
  gatewayActive?: boolean;
  allowPixDirect: boolean;
  allowCardLink: boolean;
  updatedAt?: string;
}

export interface ClientData {
  id: string;
  campaignId: string;
  targetId: string;
  name: string; // "Amanda Vasconcelos Fotógrafa em Divinópolis"
  address: string;
  rating: number;
  reviewCount: number;
  website: string | null;
  categories: string[];
  topReviews: any[];
  summary: string;
  blueprint: BlueprintItem[];
  placeUri: string;
  phoneNumber: string;
  openingHours: any;
  businessStatus: string;
  priceLevel: string | null;
  amenities: string[];
  photoAnalysis: string;
  seoKeywords: string[];
  createdAt: string;
  username?: string;
  /*
   * `pin` NAO existe mais aqui, e a ausencia e a documentacao.
   *
   * O campo declarava um PIN em texto puro dentro do objeto que as telas passam
   * de componente em componente — e enquanto ele existia no tipo, escrever nele
   * continuava sendo legal para o compilador. Duas telas faziam exatamente isso,
   * com `Math.floor(1000 + Math.random() * 9000)` no navegador.
   *
   * O que responde "esse cliente tem acesso?" e a rota `clientes.credencial`,
   * sob demanda. O segredo em si nao volta de consulta nenhuma: o que fica
   * gravado e hash argon2id.
   */
}

export interface ClientEntity {
  id: string;
  data: ClientData;
  htmlContent: string; // The index.html content extracted from zip
  slug: string; // normalized name for URL
  status: 'pending' | 'active';
  customDomain?: string | null;
  customPrice?: number | null;

  // --- vindos do site, quando a tela carregou o cliente completo -----------
  //
  // Ficam opcionais de proposito: a LISTAGEM nao carrega o site de cada
  // cliente (seria uma consulta por linha da tabela). Quem precisa do dado
  // exato — o modal, a previa — passa por `getClientById`, que carrega.

  /** Id do site. Sem ele nao da para listar nem trocar versao. */
  siteId?: string | null;
  /** Endereco publico ja resolvido pelo servidor (dominio proprio ou subdominio). */
  urlPublica?: string | null;
  /** Versao que esta no ar. E o ponteiro que `sites.reverter` troca. */
  versaoAtualId?: string | null;

  /**
   * Verdadeiro quando a senha do portal ainda e a que o operador entregou.
   *
   * So vem preenchido no LOGIN do cliente — e o servidor que responde, junto com
   * a sessao. O painel usa para abrir direto na troca de senha: senha padrao que
   * ninguem troca e senha publicada.
   */
  precisaTrocarSenha?: boolean;
}

export interface GlobalSettings {
  id?: string;
  defaultSitePrice: number;
  pixCnpj?: string | null;
  updatedAt?: string;
}

export interface Payment {
  id: string;
  clientId: string;
  amount: number;
  paymentMethod: 'card' | 'pix_auto' | 'pix_manual';
  status: 'pending' | 'paid' | 'failed' | 'verifying';
  stripePaymentIntentId?: string | null;
  receiptUrl?: string | null;
  createdAt: string;
  updatedAt?: string;
}

/**
 * As abas do painel do operador.
 *
 * `equipe` estava FALTANDO aqui — `App.tsx` já a usava em dois lugares e o
 * `setViewMode('equipe')` só não quebrava o typecheck por causa da inferência.
 * Corrigido junto com `ia` porque a próxima aba a ser acrescentada repetiria o
 * erro: a união é o único lugar que registra quais abas existem.
 */
export type ViewMode = 'dashboard' | 'upload' | 'importacao_massa' | 'criacao_massa' | 'preview' | 'report' | 'finance' | 'export_json' | 'documents' | 'proposta_builder' | 'equipe' | 'ia';

export interface ShowcaseItem {
  id: string;
  type: 'internal' | 'external';
  clientId?: string;
  externalUrl?: string; 
  externalTitle?: string;
  tabCategory: string;
}

export interface Showcase {
  id: string;
  slug: string;
  name: string;
  createdAt: string;
  items: ShowcaseItem[];
}
export enum MessageSender {
  USER = 'user',
  MODEL = 'model',
  SYSTEM = 'system',
}

export interface UrlContextMetadataItem {
  retrievedUrl: string;
  urlRetrievalStatus: string;
}

export interface ChatMessage {
  id: string;
  text: string;
  sender: MessageSender;
  timestamp: Date;
  isLoading?: boolean;
  urlContext?: UrlContextMetadataItem[];
}

export interface URLGroup {
  id: string;
  name: string;
  urls: string[];
}
